/** 
 * login方法封装
 * 依赖项：@sinolink/jssdk @sinolink/evolution
 * @Author: JinYuanche 
 * @Date: 2020-04-22 21:17:00
 * @Last Modified by: mikey.zhaopeng
 * @Last Modified time: 2020-09-14 13:37:12
 */
import axios from 'axios';
let http = axios.create();

import { getUkeyByChannelId } from '@sinolink/jssdk';
import { getAppId, checkLogin as checkLogin_, beginLogin as beginLogin_, pageInit, pageInitWithToken } from '@sinolink/evolution';
import { UserLoginParams, PidLoginParams, FundAccountLoginParams, MarginAccountLoginParams, HttpResponse } from './types/LoginTypes';
import { LoginType, AccountType } from "./enums/LoginEnums";

//api请求地址 特殊环境情况可通过全局变量修改
const API_URL = (<any>window).Login_API_URL || `${location.origin}/yjbwebservice/core`

//活动及内部通用ukey 特殊环境情况可通过全局变量修改
const CHANNELID = (<any>window).Login_CHANNELID || 'YPaJ9axolEpz';
http.interceptors.response.use(response => {
	if (response && response.data) {
		if (response.data.ERRORNO === 0) {
			return response.data.GRID0;
		} else {
			return Promise.reject(response.data);
		}
	}
	return Promise.reject(response);
})

/**
 * yjb3.0-user登录-phpsession状态同步 
 * @function userLogin
 * @param {UserLoginParams} param 
 * @returns {Promise<HttpResponse>}
 */
export function userLogin(param: UserLoginParams): Promise<HttpResponse> {
	return http.post(API_URL + '/login/user', param);
}

/**
 * yjb3.0-passportId登录-phpsession状态同步 
 * @function pidLogin
 * @param {PidLoginParams} param 
 * @returns {Promise<HttpResponse>}
 */
export function pidLogin(param: PidLoginParams): Promise<HttpResponse> {
	return http.post(API_URL + '/login/passport', param);
}


/**
 * fundaccount登录状态检查
 * @function checkFundAccountLogin
 * @returns {Promise<HttpResponse>}
 */
export function checkFundAccountLogin(): Promise<HttpResponse> {
	return http.get(API_URL + '/login/fundAccount');
}

/**
 * user登录状态检查
 * @function checkUserLogin
 * @returns {Promise<HttpResponse>}
 */
export function checkUserLogin(): Promise<HttpResponse> {
	return http.get(API_URL + '/login/user');
}

/**
 * fundaccount登录
 * @function fundAccountLogin
 * @param {FundAccountLoginParams} param 
 * @returns {Promise<HttpResponse>}
 */
export function fundAccountLogin(param: FundAccountLoginParams): Promise<HttpResponse> {
	return http.post(API_URL + '/login/fundAccount', param);
}

/**
 * marginAccount登录状态检查
 * @function checkMarginAccountLogin
 * @param {string} URL 页面URL
 * @param {boolean} needCloseCurrentView 是否需要关闭当前页面 缺省false
 * @param {object} config 打开页面参数
 * @returns {Promise<HttpResponse>}
 */
export function checkMarginAccountLogin(): Promise<HttpResponse> {
	return http.get(API_URL + '/login/marginAccount');
}
/**
 * marginAccount登录
 * @function marginAccountLogin
 * @param {MarginAccountLoginParams} param
 * @param {boolean} needCloseCurrentView 是否需要关闭当前页面 缺省false
 * @param {object} config 打开页面参数
 * @returns {Promise<HttpResponse> }
 */
export function marginAccountLogin(param: MarginAccountLoginParams): Promise<HttpResponse> {
	return http.post(API_URL + '/login/marginAccount', param);
}
/**
 * 登出
 * @function logout
 * @param {string} loginType 登录类型
 * @returns { Promise<HttpResponse> }
 */
export function logout(loginType: string | null): Promise<HttpResponse> {
	if (loginType) {
		return http.get(API_URL + `/logout/${loginType}`);
	} else {
		return http.post(API_URL + '/logout', {});
	}
}

/**
 * 初始化 （检测webapps-session数据正确性）
 * @function appInit
 * @returns {Promise<boolean>}
 */
export async function appInit(): Promise<boolean> {
	//校验金融账户体系 passportId fundAccount marginAccount
	await checkLogin(LoginType.PASSPORT);
	//校验非金融用户体系 userId
	await checkLogin(LoginType.USER);
	return true;
}
/**
 * 检查登录状态
 * @function checkLogin
 * @param {string} loginType 登录类型
 * @returns {Promise<boolean>}
 */
export async function checkLogin(loginType: string): Promise<boolean> {
	if (await getAppId() === 'yjb3.0') {
		const checkResult = await checkLogin_(loginType, CHANNELID);

		if (checkResult.code === 0) {
			let key = checkResult.result;
			if (loginType === LoginType.USER) {
				//native 处于用户登录状态 ,可直接更新phpsession保持登录状态
				let param: UserLoginParams = {
					channelId: CHANNELID,
					userUKey: key
				};
				const loginResult = await userLogin(param);
				if (loginResult.code !== 0) {
					return false;
				} else {
					return true;
				}
			} else if (loginType === LoginType.PASSPORT) {

				let param: PidLoginParams = {
					channelId: CHANNELID,
					uKey: key
				};
				const loginResult = await pidLogin(param);
				if (loginResult.code !== 0) {
					return false;
				} else {
					return true;
				}
			}
			else if (loginType === LoginType.FUNDACCOUNT) {

				key = await getUkeyByChannelId(CHANNELID);
				let param: PidLoginParams = {
					channelId: CHANNELID,
					uKey: key
				};
				const loginResult = await pidLogin(param);
				if (loginResult.code !== 0) {
					return false;
				}
				//获取当前登录状态已在evolution中完成
				// const fundAccountLoginResult = await checkFundAccountLogin();
				// if (fundAccountLoginResult.code != 0) {
				// 	//检测token
				// 	// return false;
				// 	return await loginWithToken();
				// } else {
				// 	return true;
				// }
				// return await loginWithToken();

				let instantToken = getQueryString('instant_token');
				if (instantToken) {
					return await loginWithToken();
				} else {
					const fundAccountLoginResult = await checkFundAccountLogin()
					return fundAccountLoginResult.code == 0;
				}
			}
			else if (loginType === LoginType.MARGINACCOUNT) {
				key = await getUkeyByChannelId(CHANNELID);
				let param: PidLoginParams = {
					channelId: CHANNELID,
					uKey: key
				};
				const loginResult = await pidLogin(param);
				if (loginResult.code !== 0) {
					return false;
				}
				//获取当前登录状态已在evolution中完成
				// const marginAccountLoginResult = await checkMarginAccountLogin();
				// if (marginAccountLoginResult.code != 0) {
				// 	//检测token
				// 	// return false;
				// 	return await loginWithToken();
				// } else {
				// 	return true;
				// }
				let instantToken = getQueryString('instant_token');
				if (instantToken) {
					return await loginWithToken();
				} else {
					return (await checkMarginAccountLogin()).code == 0;
				}

			}
			else {
				return false;
			}
		} else if (checkResult.code === -2) {
			//客户端系统版本不支持,或app端不支持(微信)
			return false;
		} else {
			//未登录,登录界面返回
			return false;
		}
	} else {
		//需要判断php-session状态检查登录状态

		if (loginType === LoginType.PASSPORT || loginType === LoginType.FUNDACCOUNT) {
			return await loginWithTokenForWeb(loginType);
		} else if (loginType === LoginType.MARGINACCOUNT) {
			return await loginWithTokenForWeb(loginType);
		} else if (loginType === LoginType.USER) {
			const userLoginResult = await checkUserLogin();
			if (userLoginResult.code != 0) {
				return false;
			} else {
				return true;
			}
		}
		else {
			return false;
		}

	}
}


/**
 * 检查登录状态并在未登录情况下调用登录功能
 * @function beginLogin
 * @param {string} loginType 登录类型
 * @param {string} backUrl 登录成功跳转URL（尽在未登录条件下适用，可直接跳转目标页，优化体验）
 * @param {Object} loginParam 补充参数，目前仅应用于用户登录
 * @param  {Object} loginParam.key 用户登录营销参数，格式参考4015功能号
 * @returns {Promise<boolean> | void}
 */
export async function beginLogin(loginType: string, backUrl: string,loginParam?:Object): Promise<boolean | void> {
	if (await getAppId() === 'yjb3.0') {
		const beginResult = await beginLogin_(loginType, CHANNELID,loginParam);
		if (beginResult.code === 0) {
			let key = beginResult.result;

			let needCloseCurrentView = false;
			if (!backUrl) {
				backUrl = location.href;
				needCloseCurrentView = true;
			} else if (backUrl == location.href) {
				needCloseCurrentView = true;
			}
			//backUrl = (<any>window).encodeURIComponent(backUrl);

			if (loginType === LoginType.USER) {
				//native 处于用户登录状态 ,可直接更新phpsession保持登录状态
				let param: UserLoginParams = { channelId: CHANNELID, userUKey: key };
				const loginResult = await userLogin(param);
				const isUid = loginResult.code == 0;

				return isUid;
			} else if (loginType === LoginType.PASSPORT) {

				let param: PidLoginParams = { channelId: CHANNELID, uKey: key };
				const loginResult = await pidLogin(param);
				//console.log('login:', loginResult)
				const islogin = loginResult.code == 0;
				return islogin;
			} else if (loginType === LoginType.FUNDACCOUNT) {

				key = await getUkeyByChannelId(CHANNELID);
				let param: PidLoginParams = {
					channelId: CHANNELID,
					uKey: key
				};
				const loginResult = await pidLogin(param);
				if (loginResult.code !== 0) {
					pageInitWithToken(+LoginType.FUNDACCOUNT, backUrl, needCloseCurrentView);
					await Promise.reject(new Error('no login status'));
				}
				//获取当前登录状态已在evolution中完成
				const fundAccountLoginResult = await checkFundAccountLogin();
				if (fundAccountLoginResult.code != 0) {
					//native login

					pageInitWithToken(+LoginType.FUNDACCOUNT, backUrl, needCloseCurrentView);
					await Promise.reject(new Error('no login status'));
				} else {
					return true;
				}

			} else if (loginType === LoginType.MARGINACCOUNT) {
				key = await getUkeyByChannelId(CHANNELID);
				let param: PidLoginParams = {
					channelId: CHANNELID,
					uKey: key
				};
				const loginResult = await pidLogin(param);
				if (loginResult.code !== 0) {
					pageInitWithToken(+LoginType.MARGINACCOUNT, backUrl, needCloseCurrentView);
					await Promise.reject(new Error('no login result'));
				}
				//获取当前登录状态已在evolution中完成
				const marginAccountLoginResult = await checkMarginAccountLogin();
				if (marginAccountLoginResult.code != 0) {
					//native login
					let needCloseCurrentView = false;
					if (!backUrl) {
						backUrl = location.href;
						needCloseCurrentView = true;
					} else if (backUrl == location.href) {
						needCloseCurrentView = true;
					}
					pageInitWithToken(+LoginType.MARGINACCOUNT, backUrl, needCloseCurrentView);
					await Promise.reject(new Error('no login result'));
				} else {
					return true;
				}
			} else {
				return false;
			}
		} else if (beginResult.code === -2) {
			pageInit('/me/view/download.html');
		} else {
			return false;
		}
	} else {
		if (!backUrl) backUrl = location.href;
		if (loginType === LoginType.PASSPORT || loginType === LoginType.FUNDACCOUNT) {
			const fundAccountLoginResult = await checkFundAccountLogin();

			if (fundAccountLoginResult.code != 0) {
				backUrl = deleteUrlParameter(backUrl, 'instant_token');//清理旧token或notoken
				pageInit(`${API_URL}/static/login-bridge.html?account_type=${AccountType.FUNDACCOUNT}&back_url=${encodeURIComponent(backUrl)}`);
				await Promise.reject(new Error('no login result'));
			} else {
				return true;
			}
		} else if (loginType === LoginType.MARGINACCOUNT) {
			const marginAccountLoginResult = await checkMarginAccountLogin();
			if (marginAccountLoginResult.code != 0) {
				backUrl = deleteUrlParameter(backUrl, 'instant_token');//清理旧token或notoken
				pageInit(`${API_URL}/static/login-bridge.html?account_type=${AccountType.MARGINACCOUNT}&back_url=${encodeURIComponent(backUrl)}`);
				await Promise.reject(new Error('no login status'));
			} else {
				return true;
			}
		} else if (loginType === LoginType.USER) {
			const userLoginResult = await checkUserLogin();
			if (userLoginResult.code != 0) {
				backUrl = deleteUrlParameter(backUrl, 'instant_token_v3');//清理旧token或notoken
				let userLoginBridgeUrl
				if(loginParam){
					userLoginBridgeUrl =`${API_URL}/static/login-bridge.html?account_type=${AccountType.USERID}&login_param=${encodeURIComponent(JSON.stringify(loginParam))}&back_url=${encodeURIComponent(backUrl)}`;
				}else{
					userLoginBridgeUrl =`${API_URL}/static/login-bridge.html?account_type=${AccountType.USERID}&back_url=${encodeURIComponent(backUrl)}`;
				}
				pageInit(userLoginBridgeUrl);

				await Promise.reject(new Error('no login status'));
			} else {
				return true;
			}
		}
		else {
			return false;
		}
	}
}

export async function loginWithToken() {
	const accountType = getQueryString('account_type');
	let instantToken = getQueryString('instant_token');
	if (!instantToken) {
		return false;
	}
	const oldInstantToken = localStorage.getItem('instant_token');
	if (instantToken == oldInstantToken) {
		//instantToken = '1'
		//直接返回false的前提是已经在checklogin中检测到无登录状态，且token不是最新的
		//return false;
	} else {
		//冗余逻辑
		localStorage.setItem('instant_token', instantToken);
	}
	let opStation = getQueryString('op_station');
	if (opStation) {
		localStorage.setItem('op_station', JSON.stringify(opStation));
	} else {
		opStation = 'yjbapi';
	}
	if (accountType == 1) {
		const { code } = await marginAccountLogin({
			op_station: opStation,
			account_type: 1,
			instant_token: instantToken
		});
		return code === 0;
	} else {
		const { code } = await fundAccountLogin({
			op_station: opStation,
			instant_token: instantToken
		});
		return code === 0;
	}
}
// export async function userLoginWithTokenForWeb() {
// 	const fundAccountLoginResult = await checkFundAccountLogin();
// }

export async function loginWithTokenForWeb(loginType: string) {
	const accountType = getQueryString('account_type');
	let instantToken = getQueryString('instant_token');

	const oldInstantToken = localStorage.getItem('instant_token');
	if (!instantToken || instantToken == oldInstantToken) {
		if (loginType === LoginType.PASSPORT || loginType === LoginType.FUNDACCOUNT) {
			const fundAccountLoginResult = await checkFundAccountLogin();
			if (fundAccountLoginResult.code != 0) {
				return false;
			} else {
				return true;
			}
		} else if (loginType === LoginType.MARGINACCOUNT) {
			const marginAccountLoginResult = await checkMarginAccountLogin();
			if (marginAccountLoginResult.code != 0) {
				return false;
			} else {
				return true;
			}
		}
	} else {
		instantToken !== 'notoken' ?
			localStorage.setItem('instant_token', instantToken) : null;
	}
	let opStation = getQueryString('op_station');
	if (opStation) {
		localStorage.setItem('op_station', JSON.stringify(opStation));
	} else {
		opStation = 'yjbweb';
	}
	if (accountType == 1) {
		const { code } = await marginAccountLogin({
			op_station: opStation,
			account_type: 1,
			instant_token: instantToken
		});
		return code === 0;
	} else {
		const { code } = await fundAccountLogin({
			op_station: opStation,
			instant_token: instantToken
		});
		return code === 0;
	}
}

// 获取连接参数
function getQueryString(name: string) {
	let url = window.location.search;
	let theRequest: any = new Object();
	if (url.indexOf("?") != -1) {
		let str = url.substr(url.indexOf("?") + 1),
			strs = str.split("&");
		for (let i = 0; i < strs.length; i++) {
			theRequest[strs[i].split("=")[0]] = decodeURIComponent(strs[i].split("=")[1]);
		}
	}
	return theRequest[name];
}
/**
 *
 * @param {*string} url
 * @param {*string} name 需要删除的参数名
 */
function deleteUrlParameter(url: string, name: string) {
	var urlArr = url.split('?');
	if (urlArr.length > 1 && urlArr[1].indexOf(name) > -1) {
		var query = urlArr[1];
		var obj: any = {};
		var arr: any = query.split('&');
		for (var i = 0; i < arr.length; i++) {
			arr[i] = arr[i].split('=');
			obj[arr[i][0]] = arr[i][1];
		}
		delete obj[name];
		var urlte =
			urlArr[0] +
			'?' +
			JSON.stringify(obj)
				.replace(/[\"\{\}]/g, '')
				.replace(/\:/g, '=')
				.replace(/\,/g, '&');
		return urlte;
	} else {
		return url;
	}
}
